import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { HttpStatusCode } from "axios";
import React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaPencil } from "react-icons/fa6";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { useNavigate, useParams } from "react-router-dom";
import { GlobalDataContext } from "../../../../contexts/GlobalData";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import {
  BASE_URL,
  CLINICS_EDITING,
  SEND_CLINIC_SMS,
} from "../../../../enums/ApiPaths";
import { EDIT_CLINIC, VIEW_WAREHOUSE } from "../../../../enums/PermissionTitles";
import { clinicActivation } from "../../../../enums/queryFunction/Submit";
import { CLINICS, QR_CODE_IMAGE } from "../../../../enums/QueryKeys";
import { SINGLE_CLINIC_WAREHOUSE_ID } from "../../../../enums/UrlPaths";
import useConfirm from "../../../../hooks/useConfirm";
import { snackbarSeverity } from "../../../../hooks/useSnackbar";
import useUser from "../../../../hooks/useUser";
import { useGetClinicPermissions } from "../../../../hooks/useGetClinics";
import { getBearerToken, useIsMobile } from "../../../../utils/Functions";
import { CLINIC_FORM_BUTTONS } from "../../../master/clinics/form/enums/ButtonStyles";
import ClinicNameForm from "../form/ClinicNameForm";
import usePutClinic from "../hooks/usePutClinic";
import ClinicInformation from "./ClinicInformation";
import { useTheme } from "@emotion/react";
import ClinicTiers from "./tiers/ClinicTiers";
import QrCodeDialog from "./qrCodes/QrCodeDialog";
import { useRedirectIfNotOwnClinic } from "../../../../hooks/useRedirectIfNotOwnClinic";
import logger from "../../../../utils/logger";

export default function ClinicEditingForm({ clinic }) {
  const [isFlipped, setIsFlipped] = React.useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  useRedirectIfNotOwnClinic(id);
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const queryClient = useQueryClient();
  const [isConfirmationOpen, setIsConfirmationOpen] = React.useState(false);
  const { roles } = useUser();

  const { handleSnackbar } = React.useContext(SnackbarContext);
  const hasPermission = isPermissionAllowed(EDIT_CLINIC);
  // The Warehouse shortcut is master-only; hide it when the clinic's tier
  // doesn't include the warehouse, same as the clinic's own navigation.
  const { data: clinicPermissions } = useGetClinicPermissions(id, roles.master);
  const canGoToWarehouse =
    roles.master && clinicPermissions.includes(VIEW_WAREHOUSE);

  const { control, setValue, getValues } = useForm();
   const methods = useForm({
    defaultValues: {
      clinic: clinic,
    },
  });
  const clinicEditing = usePutClinic(CLINICS_EDITING, CLINICS + id);

  const { t } = useTranslation();

  function handleFlip() {
    setIsFlipped(false);
  }

  function handleEditing() {
    setIsFlipped((prev) => !prev);
  }

  const {
    ConfirmationDialog: BaseConfirmationDialog,
    confirm,
    getMessage,
  } = useConfirm(t("SendSMS"), t("Yes"), t("No"));

  async function submitData(data) {
    const { clinic: updatedClinic } = data;
    delete updatedClinic.workingDaysList;
    delete updatedClinic.informationFields;
    delete updatedClinic.warehouse;

    if (hasPermission) {
      try {
        const res = await clinicEditing.submitCallback(updatedClinic, true);
        if (res == true) {
          handleFlip();
          queryClient.invalidateQueries({ queryKey: [QR_CODE_IMAGE, id] });
        }
      } catch (error) {
        logger.error("Saving clinic data failed:", error);
        handleSnackbar("SomethingWentWrong", snackbarSeverity.ERROR);
      }
    }
  }

  // Without this, a failed client-side validation just silently skips the
  // submit and the user is left waiting for a response that never comes.
  function handleInvalid() {
    handleSnackbar("FormError", snackbarSeverity.ERROR);
  }

  const { mutate: activation } = useMutation({
    mutationFn: (data) => clinicActivation(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries([CLINICS + id]);
      handleSnackbar("successMessage", "success");
    },
    onError: (error) => {
      handleSnackbar("errorMessage", "error");
    },
  });

  async function handleActivation() {
    getMessage(
      clinic.active ? t("ClinicDeactivateMessage") : t("ClinicActivateMessage")
    );
    const ans = await confirm();
    if (ans) {
      activation(clinic);
    }
  }

  function handleGoToWarehouse() {
    navigate(SINGLE_CLINIC_WAREHOUSE_ID.replace(":id", clinic.id));
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submitData, handleInvalid)}>
        <Stack spacing={2}>
          {clinic == null ? (
            <Skeleton variant="rectangular" width="100%" height={75} />
          ) : (
            <>
              <ResponsiveButtonBar
                handleEditing={handleEditing}
                hasPermission={hasPermission}
                clinic={clinic}
                roles={roles}
                methods={methods}
                t={t}
                handleActivation={handleActivation}
                handleGoToWarehouse={handleGoToWarehouse}
                isSaving={clinicEditing.loading}
                canGoToWarehouse={canGoToWarehouse}
              />

              {isFlipped ? (
                <ClinicNameForm clinic={clinic} />
              ) : (
                <ClinicInformation clinic={clinic} />
              )}
            </>
          )}
         <ClinicTiers />

        </Stack>
      </form>
      <BaseConfirmationDialog />
    </FormProvider>
  );
}

const ResponsiveButtonBar = ({
  handleEditing,
  hasPermission,
  clinic,
  roles,
  methods,
  t,
  handleActivation,
  handleGoToWarehouse,
  canGoToWarehouse,
  isSaving,
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Stack width="100%" spacing={2} mb={2}>
        <Stack direction="row" justifyContent="flex-end">
          <IconButton onClick={handleEditing} disabled={!hasPermission} title={t("Edit")}>
            <FaPencil size={20} />
          </IconButton>
          <QrCodeDialog clinicId={clinic.id} targetPath={clinic.url} />
        </Stack>

        <Stack direction="column" spacing={2} width="100%">
          {CLINIC_FORM_BUTTONS.map((btn) => (
            <Button
              key={btn.title}
              variant={btn.variant}
              color={btn.color}
              type={btn.type}
              disabled={!hasPermission || (btn.type === "submit" && isSaving)}
              fullWidth
              onClick={() => {
                if (btn.action === "reset") {
                  methods.reset({ clinic });
                }
              }}
            >
              {t(btn.title)}
            </Button>
          ))}

          {canGoToWarehouse && (
            <Button
              variant="outlined"
              startIcon={<WarehouseIcon />}
              onClick={handleGoToWarehouse}
              fullWidth
            >
              {t("Warehouse")}
            </Button>
          )}

          {roles.master && (
            <Button
              variant="contained"
              color={clinic.active ? "error" : "success"}
              onClick={handleActivation}
              fullWidth
            >
              {clinic.active ? t("Deactivate") : t("Activate")}
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      spacing={2}
      mb={2}
      width="100%"
    >
      {CLINIC_FORM_BUTTONS.map((btn) => (
        <Button
          key={btn.title}
          variant={btn.variant}
          color={btn.color}
          type={btn.type}
          disabled={!hasPermission || (btn.type === "submit" && isSaving)}
          onClick={() => {
            if (btn.action === "reset") {
              methods.reset({ clinic });
            }
          }}
        >
          {t(btn.title)}
        </Button>
      ))}

      {canGoToWarehouse && (
        <Button
          variant="outlined"
          startIcon={<WarehouseIcon />}
          onClick={handleGoToWarehouse}
        >
          {t("Warehouse")}
        </Button>
      )}

      {roles.master && (
        <Button
          variant="contained"
          color={clinic.active ? "error" : "success"}
          onClick={handleActivation}
        >
          {clinic.active ? t("Deactivate") : t("Activate")}
        </Button>
      )}

      <IconButton onClick={handleEditing} disabled={!hasPermission} title={t("Edit")}>
        <FaPencil size={20} />
      </IconButton>

      <QrCodeDialog clinicId={clinic.id} targetPath={clinic.url} />
    </Stack>
  );
};
